import { useEffect, useRef, useState } from 'react';

/**
 * The hero product tour: POS -> invoice -> dashboard, cycling like a demo video.
 *
 * All three screens are always rendered into the DOM and toggled with classes,
 * never conditionally mounted. That is deliberate: the marketing routes are
 * prerendered to static HTML at build time and served to crawlers that do not
 * execute JavaScript, so every word on all three screens has to exist in the
 * initial HTML. Auto-advance is presentation only, on top of content that is
 * complete without it.
 *
 * Respects prefers-reduced-motion by not auto-advancing; the tabs still work.
 */
const SCREENS = ['POS', 'Invoice', 'Dashboard'];
const INTERVAL_MS = 3600;

export default function ProductTour() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduced = useRef(false);

  useEffect(() => {
    reduced.current =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    if (paused || reduced.current) return undefined;
    // Having active in the deps restarts the countdown on every change, so a manual
    // tab click gets a full interval before the tour moves on again.
    const t = setInterval(() => setActive((a) => (a + 1) % SCREENS.length), INTERVAL_MS);
    return () => clearInterval(t);
  }, [paused, active]);

  return (
    <div
      className="pt-device"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="pt-top">
        <div className="pt-tabs" role="tablist" aria-label="Product screens">
          {SCREENS.map((label, i) => (
            <button
              key={label}
              type="button"
              role="tab"
              aria-selected={active === i}
              className={`pt-tab${active === i ? ' on' : ''}`}
              onClick={() => setActive(i)}
            >
              {label}
            </button>
          ))}
        </div>
        <span className="pt-live">LIVE</span>
      </div>

      <div className="pt-body">
        {/* ------------------------------------------------ POS ------ */}
        <div className={`pt-scr${active === 0 ? ' on' : ''}`}>
          <div className="pt-pos">
            <div className="pt-grid">
              <div className="pt-pi"><b>LED Panel 40&quot;</b><span>Rs 84,000</span></div>
              <div className="pt-pi sel"><b>Wall Bracket</b><span>Rs 3,500</span></div>
              <div className="pt-pi"><b>HDMI Cable 3m</b><span>Rs 1,200</span></div>
              <div className="pt-pi"><b>Extension Board</b><span>Rs 1,800</span></div>
              <div className="pt-pi"><b>Remote Control</b><span>Rs 950</span></div>
              <div className="pt-pi"><b>Sound Bar</b><span>Rs 22,500</span></div>
            </div>
            <div className="pt-cart">
              <div className="pt-ch">Current sale</div>
              <div className="pt-cl"><span>LED Panel 40&quot; &times;1</span><span>84,000</span></div>
              <div className="pt-cl"><span>Wall Bracket &times;1</span><span>3,500</span></div>
              <div className="pt-cl tax"><span>Sales tax 18%</span><span>15,750</span></div>
              <div className="pt-ct"><span>Total</span><span>Rs 103,250</span></div>
              <div className="pt-cb">Charge &middot; report to FBR</div>
              <div className="pt-cn"><i />Validated, ready to submit</div>
            </div>
          </div>
        </div>

        {/* -------------------------------------------- invoice ------ */}
        <div className={`pt-scr${active === 1 ? ' on' : ''}`}>
          <div className="pt-inv">
            <div className="pt-ih">
              <div>
                <b>Hassan Electronics</b>
                <span>NTN 4210148 &middot; Lahore</span>
              </div>
              <div className="pt-ir">
                <img src="/marketing/images/FBR.png" alt="Federal Board of Revenue" />
                <span>SALES TAX INVOICE</span>
              </div>
            </div>
            <table className="pt-it">
              <thead>
                <tr><th>Item</th><th>HS code</th><th className="r">Amount</th></tr>
              </thead>
              <tbody>
                <tr><td>LED Panel 40&quot;</td><td>8528.7211</td><td className="r">84,000.00</td></tr>
                <tr><td>Wall Bracket</td><td>8302.5000</td><td className="r">3,500.00</td></tr>
                <tr><td>Sales tax 18%</td><td>&mdash;</td><td className="r">15,750.00</td></tr>
              </tbody>
            </table>
            <div className="pt-itot"><span>Total</span><span>Rs 103,250.00</span></div>
            <div className="pt-if">
              <div className="pt-qr" aria-hidden="true" />
              <div className="pt-irn">
                <em>ACCEPTED BY FBR</em>
                <b>IRN 7000007DI1754821904</b>
                <span>Invoice reference number and QR code on every accepted invoice</span>
              </div>
            </div>
          </div>
        </div>

        {/* ------------------------------------------ dashboard ------ */}
        <div className={`pt-scr${active === 2 ? ' on' : ''}`}>
          <div className="pt-dash">
            <div className="pt-kpis">
              <div className="pt-k"><span>Invoices</span><b>68</b></div>
              <div className="pt-k"><span>Submitted to FBR</span><b>65</b></div>
              <div className="pt-k"><span>Total billed</span><b>Rs 2.24M</b></div>
            </div>
            <div className="pt-chart" aria-hidden="true">
              {[22, 48, 36, 72, 58, 88, 41, 64, 52, 96, 47, 70].map((h, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <i key={i} style={{ height: `${h}%` }} />
              ))}
            </div>
            <table className="pt-dt">
              <thead>
                <tr><th>Client</th><th>Invoices</th><th className="r">Total</th></tr>
              </thead>
              <tbody>
                <tr><td>Renoir</td><td>12</td><td className="r">400,680</td></tr>
                <tr><td>Marabella</td><td>7</td><td className="r">270,540</td></tr>
                <tr><td>Taj Banquet</td><td>3</td><td className="r">207,900</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="pt-dots" aria-hidden="true">
        {SCREENS.map((label, i) => (
          <i key={label} className={active === i ? 'on' : ''} />
        ))}
      </div>
    </div>
  );
}
